/**
 * Calendar-date helpers.
 *
 * Due dates are calendar dates, not instants. Everything here works in UTC and
 * normalises to midnight so that a member in Lagos (UTC+1) and a server in UTC
 * never disagree about whether a repayment is due "today".
 */
import { RepaymentFrequency } from './enums.js';
export const MS_PER_DAY = 86_400_000;
/** Normalises any Date to UTC midnight of the same calendar day. */
export function startOfUtcDay(date) {
    return new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate()));
}
export function addDays(date, days) {
    const next = startOfUtcDay(date);
    next.setUTCDate(next.getUTCDate() + days);
    return next;
}
/**
 * Adds months while clamping the day of month.
 *
 * `anchorDay` prevents cumulative drift: a schedule anchored on the 31st gives
 * 31 Jan → 28 Feb → 31 Mar, not 31 Jan → 28 Feb → 28 Mar. Without the anchor a
 * member loses a repayment day permanently the first time February is crossed.
 */
export function addMonths(date, months, anchorDay) {
    const base = startOfUtcDay(date);
    const day = anchorDay ?? base.getUTCDate();
    const target = new Date(Date.UTC(base.getUTCFullYear(), base.getUTCMonth() + months, 1));
    const daysInTargetMonth = new Date(Date.UTC(target.getUTCFullYear(), target.getUTCMonth() + 1, 0)).getUTCDate();
    target.setUTCDate(Math.min(day, daysInTargetMonth));
    return target;
}
/** Advances `date` by `periods` of the given repayment frequency. */
export function addPeriods(date, frequency, periods, anchorDay) {
    switch (frequency) {
        case RepaymentFrequency.WEEKLY:
            return addDays(date, 7 * periods);
        case RepaymentFrequency.BIWEEKLY:
            return addDays(date, 14 * periods);
        case RepaymentFrequency.MONTHLY:
            return addMonths(date, periods, anchorDay);
        case RepaymentFrequency.QUARTERLY:
            return addMonths(date, 3 * periods, anchorDay);
        default: {
            const exhaustive = frequency;
            throw new Error(`Unsupported repayment frequency: ${String(exhaustive)}`);
        }
    }
}
/** Whole days between two calendar dates. Positive when `to` is later. */
export function daysBetween(from, to) {
    return Math.round((startOfUtcDay(to).getTime() - startOfUtcDay(from).getTime()) / MS_PER_DAY);
}
/** Days a due date is past `asOf`. Zero when not yet due. */
export function daysOverdue(dueDate, asOf = new Date()) {
    return Math.max(0, daysBetween(dueDate, asOf));
}
export function isSameUtcDay(a, b) {
    return startOfUtcDay(a).getTime() === startOfUtcDay(b).getTime();
}
/** `15 September 2026` — the long form used in notifications and loan detail pages. */
export function formatLongDate(date) {
    return new Intl.DateTimeFormat('en-NG', {
        day: 'numeric',
        month: 'long',
        year: 'numeric',
        timeZone: 'UTC',
    }).format(date);
}
/**
 * `15 September 2026 at 4:12 pm` — when something happened, not just the day.
 *
 * Fixed to Lagos, because everyone reading it is there and a server drifting
 * to UTC would put an evening declaration on the wrong date.
 */
export function formatDateTime(date) {
    const day = new Intl.DateTimeFormat('en-NG', {
        day: 'numeric',
        month: 'long',
        year: 'numeric',
        timeZone: 'Africa/Lagos',
    }).format(date);
    const time = new Intl.DateTimeFormat('en-NG', {
        hour: 'numeric',
        minute: '2-digit',
        hour12: true,
        timeZone: 'Africa/Lagos',
    }).format(date);
    return `${day} at ${time}`;
}
/** `15/09/26` — the compact form used in schedule tables. */
export function formatShortDate(date) {
    return new Intl.DateTimeFormat('en-GB', {
        day: '2-digit',
        month: '2-digit',
        year: '2-digit',
        timeZone: 'UTC',
    }).format(date);
}
/** `2026-09-15` — for API payloads and date inputs. */
export function toIsoDate(date) {
    return startOfUtcDay(date).toISOString().slice(0, 10);
}
/** Parses `2026-09-15` as a UTC calendar date. */
export function fromIsoDate(value) {
    if (!/^\d{4}-\d{2}-\d{2}$/.test(value)) {
        throw new Error(`Invalid ISO date "${value}". Expected YYYY-MM-DD.`);
    }
    return new Date(`${value}T00:00:00.000Z`);
}
//# sourceMappingURL=dates.js.map